import { Info, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';

const CATEGORIES = [
  { key: 'material', label: 'Material' },
  { key: 'plant', label: 'Plant' },
  { key: 'other', label: 'Other' },
];
export const VAT_RATE = 20;

export const EMPTY_DETAILS = { category: 'material', description: '', net: '', vat_rate: VAT_RATE, gross: '', reference: '', invoice_date: '' };

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
/** Gross to send: the receipt's printed total if we have it, else net × 1.2. */
export const grossOf = (d) => (d.gross !== '' ? num(d.gross) : (d.net !== '' ? Math.round(num(d.net) * (1 + VAT_RATE / 100) * 100) / 100 : 0));
const gbp = (n) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ukDate = (iso) => (iso ? iso.split('-').reverse().join('/') : null);
const norm = (s) => (s || '').toLowerCase().replace(/\b(ltd|limited|plc|trading|group|uk|the)\b/g, '').replace(/[^a-z0-9]/g, '');

/** What the AI read from the photo — shown above the fields so the user sees it before they type. */
function ReceiptReading({ extraction, supplierName }) {
  if (!extraction || extraction.status === 'idle' || extraction.status === 'unsupported') return null;
  if (extraction.status === 'reading') {
    return (
      <div className="flex items-center gap-2 rounded-lg px-3 py-2.5 bg-[#F5A623]/10 border border-[#F5A623]/30 text-[#F5A623] text-sm" data-testid="capture-extract-reading">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading the receipt — the value, reference and date will fill in below…
      </div>
    );
  }
  if (extraction.status === 'failed') {
    return (
      <div className="flex items-center gap-2 rounded-lg px-3 py-2.5 bg-white/5 border border-white/10 text-gray-300 text-sm" data-testid="capture-extract-failed">
        <AlertTriangle className="w-4 h-4 text-gray-400" /> Couldn't read the receipt — enter the details below.
      </div>
    );
  }
  const f = extraction.fields || {};
  const conf = f.confidence || {};
  const readSupplier = f.supplier_name;
  const mismatch = readSupplier && supplierName && norm(readSupplier) && norm(supplierName) && !norm(readSupplier).includes(norm(supplierName)) && !norm(supplierName).includes(norm(readSupplier));
  const items = [
    ['Supplier', readSupplier, conf.supplier_name],
    ['Total', f.total_gross != null ? gbp(Number(f.total_gross)) : null, conf.total_gross],
    ['VAT', f.vat_amount != null && f.vat_amount > 0 ? gbp(Number(f.vat_amount)) : null, null],
    ['Ref', f.reference, conf.reference],
    ['Date', ukDate(f.date), conf.date],
  ].filter(([, v]) => v);
  return (
    <div className="rounded-lg px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/30 text-sm space-y-1.5" data-testid="capture-extract-done">
      <div className="flex items-center gap-2 text-emerald-300 font-medium"><Sparkles className="w-4 h-4" /> Read from the receipt</div>
      {items.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pl-6 text-[12px]" data-testid="capture-extract-fields">
          {items.map(([k, v, c]) => (
            <div key={k} className="flex justify-between gap-2 min-w-0">
              <span className="text-gray-400">{k}</span>
              <span className={`truncate font-medium ${c === 'low' ? 'text-amber-300' : 'text-gray-100'}`}>{v}</span>
            </div>
          ))}
        </div>
      ) : <div className="pl-6 text-[12px] text-gray-400">Nothing legible — enter the details below.</div>}
      {f.line_summary && <div className="text-[11px] text-gray-400 pl-6" data-testid="capture-extract-summary">{f.line_summary}</div>}
      {mismatch && (
        <div className="text-[11px] text-amber-300 pl-6 flex items-start gap-1" data-testid="capture-extract-supplier-mismatch">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> Receipt looks like <b className="mx-1">{readSupplier}</b> but you picked <b className="ml-1">{supplierName}</b> — go back if that's wrong.
        </div>
      )}
      {Object.values(conf).some(c => c === 'low') && <div className="text-[11px] text-amber-300 pl-6" data-testid="capture-extract-lowconf">Amber values were hard to read — please double-check.</div>}
    </div>
  );
}

const Tag = ({ show }) => (show ? <span className="text-emerald-300 text-[10px] ml-1.5" data-testid="from-receipt-tag">from receipt</span> : null);

/** Capture step 3 — optional typed details. VAT is always 20 %. */
export function CaptureDetails({ value, onChange, extraction, detailsOnly, allowPhotoOnly, supplierName, supplierField }) {
  const d = value;
  const patch = (p) => onChange({ ...d, ...p });
  const gross = grossOf(d);
  const net = d.net !== '' ? num(d.net) : (d.gross !== '' ? Math.round(num(d.gross) / (1 + VAT_RATE / 100) * 100) / 100 : 0);
  const fromReceipt = (k) => extraction?.status === 'done' && extraction.applied?.[k] != null && String(d[k]) === String(extraction.applied[k]);
  const supplierFromReceipt = supplierField && extraction?.status === 'done' && extraction.fields?.supplier_name && supplierField.value === extraction.fields.supplier_name;

  return (
    <div className="space-y-4" data-testid="capture-details">
      {!detailsOnly && <ReceiptReading extraction={extraction} supplierName={supplierName} />}

      {supplierField && (
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Supplier <span className="text-gray-500">(optional)</span><Tag show={supplierFromReceipt} /></label>
          <Input value={supplierField.value} onChange={e => supplierField.onChange(e.target.value)} placeholder={extraction?.status === 'reading' ? 'Reading from the receipt…' : 'e.g. Toolstation'}
            className="bg-[#2A3240] border-white/20 text-white h-11" data-testid="capture-supplier-name-input" />
        </div>
      )}

      <div>
        <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Category</label>
        <div className={`grid gap-2 ${allowPhotoOnly ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {CATEGORIES.map(c => (
            <button key={c.key} type="button" onClick={() => patch({ category: c.key })} data-testid={`capture-category-${c.key}`}
              className={`py-2.5 rounded-lg font-semibold text-sm transition-all active:scale-95 ${d.category === c.key ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-[#2A3240] text-gray-300 border border-white/10 hover:border-[#F5A623]/40'}`}>
              {c.label}
            </button>
          ))}
          {allowPhotoOnly && (
            <button type="button" onClick={() => patch({ category: null })} data-testid="capture-category-photo-only"
              className={`py-2.5 rounded-lg font-semibold text-sm transition-all active:scale-95 ${d.category === null ? 'bg-white/20 text-white' : 'bg-[#2A3240] text-gray-400 border border-white/10 hover:border-white/30'}`}>
              Photo only
            </button>
          )}
        </div>
        {d.category === null && <p className="text-[11px] text-gray-500 mt-1.5">Nothing is added to the job's spend — the receipt is just filed and sent.</p>}
      </div>

      {d.category && (
        <>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Description <span className="text-gray-500">(optional)</span><Tag show={fromReceipt('description')} /></label>
            <Input value={d.description} onChange={e => patch({ description: e.target.value })} placeholder="e.g. Timber 47×100 C16 ×12, screws, silicone"
              className="bg-[#2A3240] border-white/20 text-white h-11" data-testid="capture-description-input" />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Value ex VAT <span className="text-gray-500">(optional)</span><Tag show={fromReceipt('net')} /></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
              <Input type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00" value={d.net}
                onChange={e => patch({ net: e.target.value, gross: '' })}
                className="pl-7 bg-[#2A3240] border-white/20 text-white text-lg h-11" data-testid="capture-net-input" />
            </div>
            <div className="flex justify-between text-[11px] text-gray-400 px-0.5 mt-1" data-testid="capture-gross-line">
              <span>VAT 20% {gross > 0 ? gbp(Math.max(0, gross - net)) : ''}</span>
              <span>Gross <b className="text-gray-100" data-testid="capture-gross-value">{gross > 0 ? gbp(gross) : '—'}</b>{d.gross !== '' && gross > 0 && <span className="text-emerald-300 ml-1">· as printed</span>}</span>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-3">
              <label className="text-xs text-gray-400 mb-1 block">Delivery note / receipt no. <span className="text-gray-500">(optional)</span><Tag show={fromReceipt('reference')} /></label>
              <Input value={d.reference} onChange={e => patch({ reference: e.target.value })} placeholder="e.g. DN-45231" className="bg-[#2A3240] border-white/20 text-white h-11" data-testid="capture-reference-input" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-400 mb-1 block">Date<Tag show={fromReceipt('invoice_date')} /></label>
              <Input type="date" value={d.invoice_date} onChange={e => patch({ invoice_date: e.target.value })} className="bg-[#2A3240] border-white/20 text-white h-11" data-testid="capture-invoice-date-input" />
            </div>
          </div>
          <p className="text-[11px] text-gray-500 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            {detailsOnly
              ? 'Logged in the app only — nothing is sent to LiveCosts. When the supplier invoice is posted from the Cost Inbox it covers this entry, so nothing is counted twice.'
              : "The receipt goes to LiveCosts as today. When the supplier invoice is posted from the Cost Inbox it covers this entry, so nothing is counted twice."}
          </p>
        </>
      )}
    </div>
  );
}
